import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { generateNodeId } from 'common/offline/generateNodeId';
import { NodeData } from 'app/connection/api';

import { CREATE_PREMISE_DROP_ANTENNA, GetRadioOutletsResponse } from '../api';

type DropAntennaDetails = {
  id: string;
  location: string;
  photosURLs: string[];
  text: string;
  portCount: number;
  unitType: string;
  serialNumber: string;
};

type CreatePremiseDropAntennaPayload = {
  propertyId: number;
  premiseId: number;
  dropAntennaDetails: DropAntennaDetails;
};

type OptimisticDropAntennaData = {
  id: string;
  propertyId: number;
  premiseId: number;
  dropAntennaDetails: DropAntennaDetails;
};

export const useCreatePremiseDropAntenna = (propertyId: null | number, premiseId: null | number) => {
  const queryClient = useQueryClient();

  // Default values for drop antenna details (excluding id which is generated at mutation time)
  const defaultDropAntennaDetails: Omit<DropAntennaDetails, 'id'> = {
    location: '',
    photosURLs: [],
    text: '',
    portCount: 1,
    unitType: '',
    serialNumber: '',
  };

  // Optimistic update function to add drop antenna to cache immediately
  const applyOptimisticUpdate = (optimisticData: unknown) => {
    const data = optimisticData as OptimisticDropAntennaData;
    const cacheKey = ['propertyRadioConnection', propertyId];

    queryClient.setQueryData<GetRadioOutletsResponse | undefined>(cacheKey, (oldData) => {
      if (!oldData?.propertyById?.radioConnectionByRadioConnectionId?.serialized2) {
        return oldData;
      }

      const newDropAntennaNode: NodeData = {
        id: data.id,
        text: data.dropAntennaDetails.text || null,
        model: 'dropAntenna',
        group: String(data.premiseId),
        cfObject: {
          images: data.dropAntennaDetails.photosURLs,
          location: data.dropAntennaDetails.location || undefined,
        },
        portCount: data.dropAntennaDetails.portCount,
      };

      return {
        ...oldData,
        propertyById: {
          ...oldData.propertyById,
          radioConnectionByRadioConnectionId: {
            ...oldData.propertyById.radioConnectionByRadioConnectionId,
            serialized2: {
              ...oldData.propertyById.radioConnectionByRadioConnectionId.serialized2,
              nodeDataArray: [
                ...oldData.propertyById.radioConnectionByRadioConnectionId.serialized2.nodeDataArray,
                newDropAntennaNode,
              ],
            },
          },
        },
      };
    });
  };

  const mutation = useOfflineMutation<string, { message: string }, CreatePremiseDropAntennaPayload>({
    mutationFn: async (payload) => requestGQL(CREATE_PREMISE_DROP_ANTENNA, payload),
    mutationName: 'createDropAntenna',
    graphqlDocument: CREATE_PREMISE_DROP_ANTENNA,
    entity: 'dropAntenna',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [
      ['propertyRadioConnection', propertyId],
      ['dropAntennasByPropertyId', propertyId],
    ],
    getOptimisticData: (payload) => ({
      id: payload.dropAntennaDetails.id,
      ...payload,
    }),
    applyOptimisticUpdate,
  });

  return {
    ...mutation,
    mutate: () => {
      const dropAntennaId = generateNodeId();
      mutation.mutate({
        propertyId: propertyId!,
        premiseId: premiseId!,
        dropAntennaDetails: {
          ...defaultDropAntennaDetails,
          id: dropAntennaId,
        },
      });
    },
  };
};
